import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  FaAws, 
  FaCheckCircle, 
  FaShieldAlt, 
  FaRocket, 
  FaUsers, 
  FaCertificate,
  FaCloudUploadAlt,
  FaTrophy,
  FaStar,
  FaArrowRight,
  FaLightbulb
} from 'react-icons/fa';
import awsPartnerBadge from '../../assets/images/partenrbadge12.png';
import contentData from '../../data/contentData.json';

const CountUp = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      onViewportEnter={() => setIsVisible(true)}
      className="text-center"
    >
      <motion.div
        className="text-4xl md:text-5xl font-bold text-white mb-2"
        animate={{ scale: isVisible ? [1, 1.1, 1] : 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        {count}{suffix}
      </motion.div>
    </motion.div>
  );
};

const AWSPartnerSection = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const floatVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 2, 0, -2, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  const certifications = [
    { icon: FaCertificate, text: 'AWS Certified Solutions Architect', color: 'from-orange-400 to-red-500' },
    { icon: FaShieldAlt, text: 'AWS Security Specialty', color: 'from-blue-500 to-purple-600' },
    { icon: FaCloudUploadAlt, text: 'AWS DevOps Engineer', color: 'from-green-400 to-blue-500' },
    { icon: FaLightbulb, text: 'AWS Machine Learning', color: 'from-purple-500 to-pink-500' }
  ];

  return (
    <section className="relative bg-white py-24 md:py-32 overflow-hidden">
      {/* Soft Background Mesh Gradients */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-50/50 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-50/50 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2"></div>
      </div>

      {/* Floating AWS Icons - More subtle for light theme */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div 
            key={i}
            className="absolute text-blue-200/40"
            style={{
              left: `${10 + (i * 15)}%`,
              top: `${20 + (Math.sin(i) * 30)}%`,
              fontSize: `${24 + (i * 4)}px`
            }}
            variants={floatVariants}
            animate="animate"
          >
            <FaAws />
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div 
            className="text-center mb-20"
            variants={itemVariants}
          >
            <motion.div
              className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-full px-6 py-2 mb-8 shadow-sm"
              whileHover={{ y: -2 }}
            >
              <FaTrophy className="text-yellow-500 mr-2" />
              <span className="text-gray-600 font-semibold text-sm tracking-wide uppercase">AWS Advanced Tier Partner</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-8 leading-tight tracking-tight">
              Unlock the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Full Potential</span><br />
              of AWS Cloud
            </h2>
            
            <motion.p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Transform your business with our certified AWS expertise, cutting-edge solutions, and proven delivery methodologies.
            </motion.p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Badge Presentation */}
            <motion.div 
              className="relative"
              variants={itemVariants}
            >
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-[40px] blur-2xl opacity-50 group-hover:opacity-100 transition duration-1000"></div>
                
                <div className="relative bg-white p-12 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-gray-100 flex items-center justify-center">
                  <img 
                    src={awsPartnerBadge} 
                    alt="AWS Advanced Tier Partner" 
                    className="w-full max-w-[320px] h-auto transform transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Floating Elements */}
                  <div className="absolute -top-6 -right-6 w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-gray-50 transform rotate-12 group-hover:rotate-0 transition-transform duration-500">
                    <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center text-green-500">
                      <FaCheckCircle className="text-2xl" />
                    </div>
                  </div>
                  
                  <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-gray-50 transform -rotate-12 group-hover:rotate-0 transition-transform duration-500">
                    <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center text-orange-500">
                      <FaAws className="text-xl" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Certifications and CTA */}
            <motion.div 
              className="space-y-10"
              variants={itemVariants}
            >
              <div className="space-y-6">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  Your Trusted Partner for <br />
                  <span className="text-blue-600">Enterprise Cloud</span> Excellence
                </h3>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  As an official AWS Advanced Tier Partner, we provide the strategic guidance and technical execution needed to build scalable, secure, and cost-optimized cloud architectures.
                </p>
              </div>

              {/* Enhanced Certifications Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-lg transition-all duration-300 group"
                    whileHover={{ x: 8 }}
                  >
                    <div className={`w-12 h-12 bg-gradient-to-r ${cert.color} rounded-xl flex items-center justify-center mr-4 text-white shadow-md group-hover:shadow-lg transition-shadow`}>
                      <cert.icon className="text-xl" />
                    </div>
                    <span className="text-gray-800 font-semibold text-sm leading-tight">{cert.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Premium CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-5 pt-4">
                <Link 
                  to="/services/aws-services"
                  className="px-8 py-4 bg-gray-900 text-white font-bold rounded-2xl shadow-lg hover:bg-blue-700 hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center group"
                >
                  Explore AWS Services
                  <FaArrowRight className="ml-3 transform group-hover:translate-x-1 transition-transform" />
                </Link>
                
                <Link 
                  to="/contact" 
                  className="px-8 py-4 bg-white text-gray-900 font-bold rounded-2xl border-2 border-gray-200 hover:border-blue-600 hover:text-blue-600 transition-all duration-300 flex items-center justify-center"
                >
                  Consult Our Experts
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AWSPartnerSection; 